import type { AmlReview } from '@/integrations/Immersve/aml-review/types';
import type { ImmersveApiClient } from '@/hooks/useImmersveApiClient';
import { tenantRegionForKycConfigId } from '@/integrations/Immersve/kycConfigRegions';
import type { CaseIdentityDetails, HitRecord, IdentityFieldDiff } from './caseDetail';
import { frankieOneToAmlHits } from './frankieOneKycCheck';
import { sumsubToAmlHits } from './sumsubKycCheck';

export interface HitEnrichment {
  matchedName: string;
  diff?: IdentityFieldDiff[];
  records?: HitRecord[];
}

export type HitEnrichmentResult =
  | { status: 'ok'; hitsById: Map<string, HitEnrichment> }
  | { status: 'unavailable'; reason: string };

export async function resolveHitEnrichment(
  client: ImmersveApiClient,
  review: AmlReview,
  identityDetails: CaseIdentityDetails
): Promise<HitEnrichmentResult> {
  const configRegion = tenantRegionForKycConfigId(review.kycConfigId);
  if (!configRegion) {
    return { status: 'unavailable', reason: `Unrecognized kycConfigId "${review.kycConfigId}"` };
  }
  if (configRegion.kycProvider !== review.kycProvider) {
    return {
      status: 'unavailable',
      reason: `kycConfigId resolves to ${configRegion.kycProvider}, but this review's provider is ${review.kycProvider}`,
    };
  }

  try {
    if (configRegion.kycProvider === 'frankieOne') {
      if (configRegion.tenantRegion === 'lac') {
        return {
          status: 'unavailable',
          reason: "FrankieOne 'lac' tenant region is not yet supported by the kyc-check endpoint",
        };
      }
      const kycCheck = await client.getFrankieOneKycCheck(
        configRegion.tenantRegion,
        review.externalProfileId
      );
      const hits = frankieOneToAmlHits(kycCheck, identityDetails);
      return { status: 'ok', hitsById: toHitsById(hits) };
    }

    const kycCheck = await client.getSumsubKycCheck(
      configRegion.tenantRegion,
      review.externalProfileId
    );
    if (!kycCheck.amlCase) {
      return { status: 'unavailable', reason: 'Sumsub returned no AML case for this applicant' };
    }
    const hits = sumsubToAmlHits(kycCheck.amlCase, identityDetails);
    return { status: 'ok', hitsById: toHitsById(hits) };
  } catch (err) {
    return { status: 'unavailable', reason: `kyc-check request failed: ${(err as Error).message}` };
  }
}

function toHitsById(
  hits: { id: string; matchedName: string; diff?: IdentityFieldDiff[]; records?: HitRecord[] }[]
): Map<string, HitEnrichment> {
  const hitsById = new Map<string, HitEnrichment>();
  for (const hit of hits) {
    hitsById.set(hit.id, { matchedName: hit.matchedName, diff: hit.diff, records: hit.records });
  }
  return hitsById;
}
